import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Leaf, Zap, Shield, TrendingDown, ChevronDown, ChevronUp } from 'lucide-react';

// Hook pour détecter si un élément est visible
const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isInView];
};

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  
  // Refs pour les animations au scroll
  const [statsRef, statsInView] = useInView(0.2);
  const [solutionsRef, solutionsInView] = useInView(0.1);
  const [whyRef, whyInView] = useInView(0.1);
  const [projectsRef, projectsInView] = useInView(0.1);
  const [faqRef, faqInView] = useInView(0.1);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const faqData = [
    {
      question: 'Quelles sont les aides disponibles en 2025 ?',
      answer: 'Vous pouvez bénéficier de MaPrimeRénov (jusqu\'à 10 000€), des primes CEE, de l\'Éco-PTZ et de la TVA à 5.5%. Nouveau Départ gère l\'ensemble de ces démarches pour vous.'
    },
    {
      question: 'Combien coûte une rénovation énergétique ?',
      answer: 'Le coût dépend de votre logement et des travaux. Grâce aux aides de l\'État, le reste à charge peut être réduit jusqu\'à 90%. Demandez votre devis gratuit !'
    },
    {
      question: 'Comment se déroule l\'installation ?',
      answer: 'Nos équipes certifiées RGE interviennent en 1 à 3 jours selon les travaux. Nous assurons la mise en service et le nettoyage du chantier.'
    },
    {
      question: 'Pourquoi choisir un artisan RGE ?',
      answer: 'La certification RGE est indispensable pour bénéficier des aides. Elle garantit la qualité des travaux. Tous nos installateurs sont certifiés RGE.'
    },
    {
      question: 'Quelle est la durée de garantie ?',
      answer: 'Garantie décennale sur l\'installation et jusqu\'à 25 ans sur les panneaux solaires. Les pompes à chaleur bénéficient d\'une garantie de 2 à 5 ans.'
    }
  ];

  const projects = [
    {
      title: 'Isolation Extérieure Pavillon',
      location: 'Paris 17e',
      type: 'Isolation ITE',
      image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800'
    },
    {
      title: 'Installation Panneaux Solaires 6kWc',
      location: 'Versailles',
      type: 'Solaire',
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800'
    },
    {
      title: 'Pompe à Chaleur Air/Eau',
      location: 'Boulogne',
      type: 'Chauffage',
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=800'
    },
    {
      title: 'Isolation Combles Perdus',
      location: 'Neuilly',
      type: 'Isolation',
      image: 'https://images.unsplash.com/photo-1562259929-b4e1fd3aef09?w=800'
    }
  ];

  const solutions = [
    {
      title: 'Panneaux Solaires',
      description: 'Produisez votre propre électricité et réduisez vos factures jusqu\'à 70%',
      icon: '☀️',
      link: '/solutions/solaire',
      color: '#F59E0B'
    },
    {
      title: 'Pompe à Chaleur',
      description: 'Chauffage écologique avec des économies de 60 à 75% sur votre facture',
      icon: '🌡️',
      link: '/solutions/chauffage',
      color: '#EF4444'
    },
    {
      title: 'Isolation Thermique',
      description: 'Confort optimal été comme hiver et économies durables',
      icon: '🏠',
      link: '/solutions/isolation',
      color: '#3B82F6'
    }
  ];

  const stats = [
    { number: '5000+', label: 'Projets réalisés' },
    { number: '98%', label: 'Clients satisfaits' },
    { number: '15 ans', label: 'd\'expérience' },
    { number: '50%', label: 'd\'économies moyennes' }
  ];

  return (
    <div style={{ background: 'var(--bg-page)', overflow: 'hidden' }}>
      {/* Styles pour les animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translateX(-40px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(40px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        
        .animate-fadeInLeft {
          animation: fadeInLeft 0.8s ease-out forwards;
        }
        
        .animate-fadeInRight {
          animation: fadeInRight 0.8s ease-out forwards;
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.6s ease-out forwards;
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .delay-1 { animation-delay: 0.1s; }
        .delay-2 { animation-delay: 0.2s; }
        .delay-3 { animation-delay: 0.3s; }
        .delay-4 { animation-delay: 0.4s; }
        .delay-5 { animation-delay: 0.5s; }
        
        .hero-title {
          opacity: 0;
        }
        
        .hero-title.visible {
          animation: fadeInUp 1s ease-out forwards;
        }
        
        .hero-subtitle {
          opacity: 0;
        }
        
        .hero-subtitle.visible {
          animation: fadeInUp 1s ease-out 0.3s forwards;
        }
        
        .hero-buttons {
          opacity: 0;
        }
        
        .hero-buttons.visible {
          animation: fadeInUp 1s ease-out 0.5s forwards;
        }
        
        .section-hidden {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease-out;
        }
        
        .section-visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        .card-animate {
          opacity: 0;
          transform: translateY(30px);
        }
        
        .card-animate.visible {
          opacity: 1;
          transform: translateY(0);
          transition: all 0.6s ease-out;
        }
        
        .stat-number {
          display: inline-block;
        }
        
        .solution-card {
          transition: all 0.3s ease;
        }
        
        .solution-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0, 69, 52, 0.15);
        }
        
        .project-card {
          transition: all 0.3s ease;
        }
        
        .project-card:hover {
          transform: scale(1.03);
        }
        
        .wave-divider {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          overflow: hidden;
          line-height: 0;
        }
        
        .wave-divider svg {
          position: relative;
          display: block;
          width: calc(100% + 1.3px);
          height: 80px;
        }
      `}</style>

      {/* Hero Section */}
      <section style={{ 
        position: 'relative', 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        overflow: 'hidden', 
        paddingTop: '120px' 
      }}>
        {/* Video Background */}
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
          <video 
            autoPlay 
            muted 
            loop 
            playsInline
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover'
            }}
          >
            <source src="https://cdn.pixabay.com/video/2020/05/02/37907-415263544_large.mp4" type="video/mp4" />
          </video>
          <div style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%', 
            background: 'linear-gradient(135deg, rgba(0, 69, 52, 0.85) 0%, rgba(12, 105, 81, 0.75) 100%)' 
          }} />
        </div>

        <div className="container" style={{ position: 'relative', zIndex: 1, textAlign: 'center', color: 'white', padding: '0 20px' }}>
          <h1 
            className={`hero-title ${isVisible ? 'visible' : ''}`}
            style={{ 
              fontSize: 'clamp(2.5rem, 6vw, 4rem)', 
              fontWeight: '700', 
              color: 'white', 
              marginBottom: '24px',
              lineHeight: 1.2
            }}
          >
            Réduisez Vos Factures<br />
            <span style={{ color: 'var(--brand-primary)' }}>Jusqu'à 70%</span>
          </h1>
          
          <p 
            className={`hero-subtitle ${isVisible ? 'visible' : ''}`}
            style={{ 
              color: 'white', 
              maxWidth: '700px', 
              margin: '0 auto 48px', 
              opacity: 0.95,
              fontSize: 'clamp(1rem, 2vw, 1.25rem)',
              lineHeight: 1.7
            }}
          >
            Expert en rénovation énergétique : isolation, chauffage écologique et panneaux solaires. 
            Profitez des aides de l'État avec un accompagnement complet de A à Z.
          </p>
          
          <div 
            className={`hero-buttons ${isVisible ? 'visible' : ''}`}
            style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}
          >
            <Link 
              to="/devis" 
              className="network-btn animate-float"
              style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '8px',
                padding: '16px 32px',
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: '50px',
                background: 'var(--brand-primary)',
                color: 'var(--brand-dark)',
                textDecoration: 'none',
                transition: 'all 0.3s ease'
              }}
            >
              Devis Gratuit <ArrowRight size={18} />
            </Link>
            <Link 
              to="/solutions" 
              style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '8px',
                padding: '16px 32px',
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: '50px',
                background: 'transparent',
                color: 'white',
                textDecoration: 'none',
                border: '2px solid white',
                transition: 'all 0.3s ease'
              }}
            >
              Découvrir nos solutions
            </Link>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="wave-divider">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path 
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C57.1,118.92,156.63,69.08,321.39,56.44Z" 
              fill="#f5f5f5"
            />
          </svg>
        </div>
      </section>

      {/* Stats Section */}
      <section 
        ref={statsRef}
        style={{ 
          padding: '80px 20px', 
          background: '#f5f5f5',
          transform: statsInView ? 'translateY(0)' : 'translateY(30px)',
          opacity: statsInView ? 1 : 0,
          transition: 'all 0.8s ease-out'
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '32px',
            textAlign: 'center'
          }}>
            {stats.map((stat, index) => (
              <div 
                key={index}
                style={{
                  padding: '24px',
                  opacity: statsInView ? 1 : 0,
                  transform: statsInView ? 'translateY(0)' : 'translateY(20px)',
                  transition: `all 0.6s ease-out ${index * 0.1}s`
                }}
              >
                <div style={{ 
                  fontSize: 'clamp(2rem, 5vw, 3rem)', 
                  fontWeight: '700', 
                  color: 'var(--brand-dark)',
                  marginBottom: '8px'
                }}>
                  {stat.number}
                </div>
                <div style={{ fontSize: '16px', color: '#666' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section 
        ref={solutionsRef}
        style={{ 
          padding: '100px 20px', 
          background: 'white',
          opacity: solutionsInView ? 1 : 0,
          transform: solutionsInView ? 'translateY(0)' : 'translateY(30px)',
          transition: 'all 0.8s ease-out'
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{ 
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', 
              fontWeight: '700', 
              color: 'var(--brand-dark)',
              marginBottom: '16px'
            }}>
              Nos Solutions Énergétiques
            </h2>
            <p style={{ color: '#666', maxWidth: '600px', margin: '0 auto', fontSize: '18px' }}>
              Des solutions sur mesure pour réduire vos factures et votre impact environnemental
            </p>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '32px' 
          }}>
            {solutions.map((solution, index) => (
              <Link 
                key={index}
                to={solution.link}
                className="solution-card"
                style={{
                  background: 'white',
                  borderRadius: '20px',
                  padding: '40px 32px',
                  textDecoration: 'none',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  border: '1px solid #eee',
                  opacity: solutionsInView ? 1 : 0,
                  transform: solutionsInView ? 'translateY(0)' : 'translateY(30px)',
                  transition: `all 0.6s ease-out ${index * 0.15}s`
                }}
              >
                <div style={{ 
                  fontSize: '48px', 
                  marginBottom: '20px'
                }}>
                  {solution.icon}
                </div>
                <h3 style={{ 
                  fontSize: '22px', 
                  fontWeight: '600', 
                  color: 'var(--brand-dark)',
                  marginBottom: '12px'
                }}>
                  {solution.title}
                </h3>
                <p style={{ color: '#666', lineHeight: 1.6, marginBottom: '20px' }}>
                  {solution.description}
                </p>
                <span style={{ 
                  color: 'var(--brand-dark)', 
                  fontWeight: '600',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  En savoir plus <ArrowRight size={16} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section 
        ref={whyRef}
        style={{ 
          padding: '100px 20px', 
          background: 'var(--brand-dark)',
          color: 'white',
          opacity: whyInView ? 1 : 0,
          transform: whyInView ? 'translateY(0)' : 'translateY(30px)',
          transition: 'all 0.8s ease-out'
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{ 
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', 
              fontWeight: '700',
              marginBottom: '16px'
            }}>
              Pourquoi Choisir Nouveau Départ ?
            </h2>
            <p style={{ opacity: 0.9, maxWidth: '600px', margin: '0 auto', fontSize: '18px' }}>
              Une expertise reconnue et des garanties solides pour votre projet
            </p>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '32px' 
          }}>
            {[
              { icon: <Shield size={32} />, title: 'Certifié RGE', desc: 'Garantie de qualité et accès aux aides' },
              { icon: <TrendingDown size={32} />, title: 'Économies Garanties', desc: 'Jusqu\'à 70% de réduction sur vos factures' },
              { icon: <Leaf size={32} />, title: 'Éco-responsable', desc: 'Solutions durables pour la planète' },
              { icon: <Zap size={32} />, title: 'Installation Rapide', desc: 'Travaux réalisés en 1 à 3 jours' }
            ].map((item, index) => (
              <div 
                key={index}
                style={{
                  textAlign: 'center',
                  padding: '32px 24px',
                  borderRadius: '16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  opacity: whyInView ? 1 : 0,
                  transform: whyInView ? 'translateY(0)' : 'translateY(20px)',
                  transition: `all 0.6s ease-out ${index * 0.1}s`
                }}
              >
                <div style={{ 
                  color: 'var(--brand-primary)', 
                  marginBottom: '16px',
                  display: 'inline-block'
                }}>
                  {item.icon}
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>
                  {item.title}
                </h3>
                <p style={{ opacity: 0.8, fontSize: '15px' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Gallery */}
      <section 
        ref={projectsRef}
        style={{ 
          padding: '100px 20px', 
          background: '#f5f5f5',
          opacity: projectsInView ? 1 : 0,
          transform: projectsInView ? 'translateY(0)' : 'translateY(30px)',
          transition: 'all 0.8s ease-out'
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{ 
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', 
              fontWeight: '700', 
              color: 'var(--brand-dark)',
              marginBottom: '16px'
            }}>
              Nos Réalisations
            </h2>
            <p style={{ color: '#666', maxWidth: '600px', margin: '0 auto', fontSize: '18px' }}>
              Découvrez quelques-uns de nos projets récents
            </p>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
            gap: '24px' 
          }}>
            {projects.map((project, index) => (
              <div 
                key={index}
                className="project-card"
                style={{
                  borderRadius: '16px',
                  overflow: 'hidden',
                  background: 'white',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                  opacity: projectsInView ? 1 : 0,
                  transform: projectsInView ? 'translateY(0)' : 'translateY(30px)',
                  transition: `all 0.6s ease-out ${index * 0.1}s`
                }}
              >
                <div style={{ height: '200px', overflow: 'hidden' }}>
                  <img 
                    src={project.image} 
                    alt={project.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
                <div style={{ padding: '20px' }}>
                  <span style={{ 
                    display: 'inline-block',
                    padding: '4px 12px',
                    background: 'var(--brand-primary)',
                    color: 'var(--brand-dark)',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    marginBottom: '12px'
                  }}>
                    {project.type}
                  </span>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--brand-dark)', marginBottom: '8px' }}>
                    {project.title}
                  </h3>
                  <p style={{ color: '#666', fontSize: '14px' }}>{project.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section 
        ref={faqRef}
        style={{ 
          padding: '100px 20px', 
          background: 'white',
          opacity: faqInView ? 1 : 0,
          transform: faqInView ? 'translateY(0)' : 'translateY(30px)',
          transition: 'all 0.8s ease-out'
        }}
      >
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{ 
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', 
              fontWeight: '700', 
              color: 'var(--brand-dark)',
              marginBottom: '16px'
            }}>
              Questions Fréquentes
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {faqData.map((faq, index) => (
              <div 
                key={index}
                style={{
                  background: '#f9f9f9',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid #eee',
                  opacity: faqInView ? 1 : 0,
                  transform: faqInView ? 'translateY(0)' : 'translateY(20px)',
                  transition: `all 0.5s ease-out ${index * 0.1}s`
                }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  style={{
                    width: '100%',
                    padding: '20px 24px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    textAlign: 'left'
                  }}
                >
                  <span style={{ fontSize: '16px', fontWeight: '600', color: 'var(--brand-dark)' }}>
                    {faq.question}
                  </span>
                  {openFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openFaq === index && (
                  <div style={{ 
                    padding: '0 24px 20px',
                    color: '#666',
                    lineHeight: 1.7
                  }}>
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{ 
        padding: '100px 20px', 
        background: 'linear-gradient(135deg, var(--brand-dark) 0%, #0C6951 100%)',
        textAlign: 'center',
        color: 'white'
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h2 style={{ 
            fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', 
            fontWeight: '700',
            marginBottom: '20px'
          }}>
            Prêt à Réduire Vos Factures ?
          </h2>
          <p style={{ fontSize: '18px', opacity: 0.9, marginBottom: '40px' }}>
            Obtenez votre estimation gratuite en quelques minutes
          </p>
          <Link 
            to="/devis"
            className="animate-float"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '18px 40px',
              background: 'var(--brand-primary)',
              color: 'var(--brand-dark)',
              borderRadius: '50px',
              fontWeight: '600',
              fontSize: '18px',
              textDecoration: 'none',
              transition: 'all 0.3s ease'
            }}
          >
            Demander un Devis Gratuit <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
